import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  getObituarySearch,
  type ObituaryTeaserParams,
} from '@ltvco/refresh-lib/v1';
import { useCallback } from 'react';

import type { ObituaryTeaserParamsForHandler } from 'components/ChatBV/utils/conversationUtils';

/** Treat empty, "null", or values containing "null"/"unknown" as missing (do not send to API). */
function hasValue(value: string) {
  if (value === null || value === undefined || typeof value !== 'string') {
    return false;
  }
  const trimmedValue = value.trim().toLowerCase();
  return (
    trimmedValue !== '' &&
    trimmedValue !== 'null' &&
    !trimmedValue.includes('null') &&
    !trimmedValue.includes('unknown')
  );
}

/** Name is required; only reject if missing or exactly the string "null". */
function hasName(value: string) {
  if (value === null || value === undefined || typeof value !== 'string') {
    return false;
  }
  const trimmedValue = value.trim().toLowerCase();
  return trimmedValue !== '' && trimmedValue !== 'null';
}

function toObituarySearchParams(
  params: ObituaryTeaserParamsForHandler
): ObituaryTeaserParams {
  const result: Record<string, string> = {};
  if (hasName(params.name)) {
    result.name = params.name.trim();
  }
  if (hasValue(params?.dob ?? '')) {
    result.dob = (params?.dob ?? '').trim();
  }
  if (hasValue(params?.dod ?? '')) {
    result.dod = (params?.dod ?? '').trim();
  }
  if (hasValue(params?.location ?? '')) {
    result.location = (params?.location ?? '').trim();
  }
  return result as unknown as ObituaryTeaserParams;
}

/**
 * Returns searchObituary for handleFunctionCalls (obituary teaser).
 * Uses getObituarySearch from lib with brand base URL from context.
 */
export function useObituarySearchOption(): (
  params: ObituaryTeaserParamsForHandler
) => Promise<{ results: unknown[] | []; count: number } | undefined> {
  const { data: appConstantsData } = useAppConstantsContext();
  const brandLink =
    (appConstantsData as { links?: { baseUrl?: string } } | undefined)?.links
      ?.baseUrl ?? '';

  return useCallback(
    async (params: ObituaryTeaserParamsForHandler) => {
      if (!hasName(params.name)) {
        return undefined;
      }
      try {
        const obituarySearchParams = toObituarySearchParams(params);
        const paramsRecord = obituarySearchParams as unknown as Record<
          string,
          string
        >;
        if (!paramsRecord.name) {
          return undefined;
        }
        const data = await getObituarySearch(
          { obituarySearchParams },
          brandLink
        );
        const raw = data as {
          entities?: { options?: { obituaries?: unknown[] } };
        };
        const list = raw?.entities?.options?.obituaries ?? [];
        const results = Array.isArray(list) ? list : [];
        if (!results.length) {
          return undefined;
        }
        return { results: results as unknown[], count: results.length };
      } catch {
        return undefined;
      }
    },
    [brandLink]
  );
}
